import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNotification } from "../../../../../hooks/useNotification";
import { useHandleMilestoneViewMutation } from "../../../../../store/apiSlice/newInterfaceApiSlice";
import { ConsignmentTrackingControl } from "../../../../oceanTracking/TrackingPanel";
import {
  canDropOnPurchaseCombinedColumn,
  groupPurchaseOrdersByCombinedLanes,
} from "../../../../purchaseOrder/constants/poCombinedBoard.js";
import {
  canDropOnCombinedColumn,
  columnStorageKey,
  enrichCombinedColumns,
  groupShipmentsByCombinedLanes,
  normalizeCombinedBoardSequence,
  shouldHideEmptyLane,
} from "../../../../shipment/constants/shipmentCombinedBoard.js";
import { orderTypeForUrl, selectState } from "../../utils/tableDataElements";
import { BoardCardIcons } from "../Board/BoardCardIcons";
import {
  BoardRelationshipGraphDrawer,
  graphNodeTypeForOrderType,
} from "../Board/BoardRelationshipGraphDrawer";
import { BoardLaneModeIcon } from "../Board/BoardLaneModeIcon.jsx";
import { PurchaseOrderBoardCard } from "../Board/PurchaseOrderBoardCard";
import { ShipmentBoardCardTitle } from "../Board/ShipmentBoardCardTitle";
import { ShipmentBoardFooterMeta } from "../Board/ShipmentBoardFooterMeta";
import {
  resolveBoardCardFooterLabel,
  resolveBoardCardHeroText,
  useBoardCardHero,
} from "../Board/boardCardDisplay";
import { NotesDrawerShell } from "../Notes/NotesDrawerShell";
import { FilesDrawerShell } from "../PurchaseOrderDetails/Files/FilesDrawerShell";
import { Status } from "../Status/Status";

const DRAG_MIME = "application/shipment-combined-board";

/**
 * Shipment board with interleaved status and milestone columns.
 */
export function CombinedShipmentBoard({
  selectedCurrentView,
  shipmentOrders,
  getConsignmentByRule,
  handleSelectOrder,
  hideEmptyLanesMode = "none",
  orderType = "shipment",
}) {
  const statusPalette =
    orderType === "purchase" || orderType === "shipment" ? "journey" : undefined;
  const boardCardHero = useBoardCardHero(selectedCurrentView, orderType);
  const { milestoneList } = useSelector(state => state[selectState[orderType]] || {});
  const combinedViewSequences = useSelector(
    state => state.newInterface.allViews.boardView?.combined?.allViews || {},
  );

  const [updateMilestoneView, { isLoading: moving }] = useHandleMilestoneViewMutation();
  const notify = useNotification();

  const [isOpenNotes, setIsOpenNotes] = useState(false);
  const [isOpenFiles, setIsOpenFiles] = useState(false);
  const [orderIdForNotes, setOrderIdForNotes] = useState(null);
  const [orderIdForFiles, setOrderIdForFiles] = useState(null);
  const [graphRecord, setGraphRecord] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const columns = useMemo(() => {
    const sequence =
      combinedViewSequences[selectedCurrentView.value] || selectedCurrentView.sequence || [];
    return enrichCombinedColumns(normalizeCombinedBoardSequence(sequence), milestoneList);
  }, [
    combinedViewSequences,
    milestoneList,
    selectedCurrentView.sequence,
    selectedCurrentView.value,
  ]);

  const groupedOrders = useMemo(
    () =>
      orderType === "purchase"
        ? groupPurchaseOrdersByCombinedLanes(shipmentOrders, columns)
        : groupShipmentsByCombinedLanes(shipmentOrders, columns),
    [columns, orderType, shipmentOrders],
  );

  const visibleColumns = useMemo(() => {
    return columns.filter(column => {
      const key = columnStorageKey(column);
      const count = groupedOrders[key]?.length || 0;
      const laneKind = column.kind === "milestone" ? "milestone" : "status";
      return !shouldHideEmptyLane(hideEmptyLanesMode, laneKind, count);
    });
  }, [columns, groupedOrders, hideEmptyLanesMode]);

  const openNotesModal = (id, orderName, is_note_available, open_task_count = 0) => {
    setIsOpenNotes(true);
    setOrderIdForNotes({ id, name: orderName, is_note_available, open_task_count });
  };
  const closeNotesModal = () => {
    setIsOpenNotes(false);
    setOrderIdForNotes(null);
  };
  const openFilesDrawer = (id, orderName, recordDisplayName = "") => {
    setOrderIdForFiles({ id, name: orderName, recordDisplayName });
    setIsOpenFiles(true);
  };
  const closeFilesDrawer = () => {
    setIsOpenFiles(false);
    setOrderIdForFiles(null);
  };
  const openGraphDrawer = (id, orderName) => {
    setGraphRecord({ id, name: orderName });
  };
  const closeGraphDrawer = () => {
    setGraphRecord(null);
  };

  const handleDragStart = (e, order) => {
    if (moving) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ orderId: order.id, status: order.status }));
  };

  const handleDragOver = (e, column) => {
    if (!e.dataTransfer.types.includes(DRAG_MIME)) {
      return;
    }
    e.preventDefault();
    setDragOverKey(columnStorageKey(column));
  };

  const handleDragLeave = () => {
    setDragOverKey(null);
  };

  const handleDrop = useCallback(
    async (e, targetColumn) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverKey(null);
      if (moving) {
        return;
      }
      let payload;
      try {
        payload = JSON.parse(e.dataTransfer.getData(DRAG_MIME));
      } catch (_) {
        return;
      }
      const order = shipmentOrders.find(item => item.id === payload.orderId);
      const canDrop =
        orderType === "purchase"
          ? canDropOnPurchaseCombinedColumn(order, targetColumn, columns)
          : canDropOnCombinedColumn(order, targetColumn, columns);
      if (!order || !canDrop) {
        return;
      }

      try {
        if (targetColumn.kind === "status") {
          await updateMilestoneView({
            id: "Uncategorized",
            data: {
              ids: [order.id],
              module: orderTypeForUrl[orderType].module,
            },
          }).unwrap();
          return;
        }

        await updateMilestoneView({
          id: targetColumn.id,
          sourceIndex: targetColumn.id,
          data: {
            ids: [order.id],
            module: orderTypeForUrl[orderType].module,
          },
        }).unwrap();
      } catch (error) {
        const data = error?.data || {};
        notify({
          type: "error",
          message: data.detail || "Failed to update milestone.",
        });
      }
    },
    [columns, moving, notify, orderType, shipmentOrders, updateMilestoneView],
  );

  if (!columns.length) {
    return null;
  }

  return (
    <>
      <div className="boardView combinedBoardView">
        {visibleColumns.map(column => {
          const laneKey = columnStorageKey(column);
          const orders = groupedOrders[laneKey] || [];
          const isMilestone = column.kind === "milestone";
          const laneClassName = [
            "tilesContainer",
            dragOverKey === laneKey ? "tilesContainerDragOver" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const boardHeaderClassName = isMilestone
            ? "boardHeader"
            : "boardHeader boardHeaderWithModeIcon";

          return (
            <div key={laneKey} className="board">
              <div className={boardHeaderClassName}>
                {isMilestone ? (
                  <h3 className={`colorBox ${column.color || ""} boardHeaderWithModeIcon`}>
                    <BoardLaneModeIcon kind="milestone" />
                    <p className="tickBox"></p>
                    <p>{column.name}</p>
                  </h3>
                ) : (
                  <div className="boardHeaderLaneTitle">
                    <BoardLaneModeIcon kind="status" statusLabel={column.key} />
                    <Status statusName={column.key} palette={statusPalette} />
                  </div>
                )}
                <p className="countNo">{orders.length}</p>
              </div>
              <div
                className={laneClassName}
                onClick={handleSelectOrder}
                onDragOver={e => handleDragOver(e, column)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, column)}
              >
                {orders.map(order =>
                  orderType === "purchase" ? (
                    <PurchaseOrderBoardCard
                      key={order.id}
                      order={order}
                      orderType={orderType}
                      boardCardHero={boardCardHero}
                      draggable={!moving}
                      onDragStart={e => handleDragStart(e, order)}
                      onOpenFiles={openFilesDrawer}
                      onOpenNotes={openNotesModal}
                      onOpenGraph={openGraphDrawer}
                    />
                  ) : (
                    <div
                      key={order.id}
                      className="boardTile shipmentBoardTile"
                      draggable={!moving}
                      onDragStart={e => handleDragStart(e, order)}
                    >
                      <div className="tileHeader">
                        <div className="left">
                          <input
                            className="checkBox"
                            type="checkbox"
                            value={JSON.stringify(order)}
                          />
                          <ShipmentBoardCardTitle
                            orderId={order.id}
                            name={resolveBoardCardHeroText(order, orderType, boardCardHero)}
                          />
                        </div>
                        <BoardCardIcons
                          order={order}
                          onOpenFiles={openFilesDrawer}
                          onOpenNotes={openNotesModal}
                          onOpenGraph={
                            graphNodeTypeForOrderType(orderType) ? openGraphDrawer : undefined
                          }
                        />
                      </div>
                      <div className="facilityAndAsn">
                        <div className="destinationFacility">
                          <span>Destination facility</span>
                          <strong>{order.to_warehouse?.name || order.to_warehouse || "—"}</strong>
                        </div>
                        <div className="shipmentDetails">
                          <div>
                            <span>ASN</span>
                            <strong>{order.asn?.name || "Pending"}</strong>
                          </div>
                        </div>
                      </div>
                      <div className="tileFooter">
                        <ShipmentBoardFooterMeta
                          orderId={order.id}
                          reference={resolveBoardCardFooterLabel(order, orderType, boardCardHero)}
                          etaDisplay={
                            order.eta || order.estimated_date
                              ? format(order.eta || order.estimated_date, "dd MMM yyyy, hh:mm a")
                              : "Pending"
                          }
                        />
                        <div className="trackerActions">
                          <ConsignmentTrackingControl
                            consignment={order.id}
                            shipmentNumber={order.name}
                            onTrackingUpdated={getConsignmentByRule}
                            deferQuery
                            initialHasTracking={order.has_ocean_tracking}
                            renderTrigger={({ disabled, hasTracking, label, onClick }) => (
                              <button
                                className="trackDetails"
                                type="button"
                                disabled={disabled}
                                title={label}
                                onClick={onClick}
                              >
                                {hasTracking ? "Show Tracker" : "Track"}{" "}
                                <span aria-hidden="true">→</span>
                              </button>
                            )}
                          />
                          <div className="statusAndPriority">
                            <Status statusName={order.status} palette={statusPalette} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          );
        })}
      </div>
      <NotesDrawerShell
        isOpen={isOpenNotes && Boolean(orderIdForNotes)}
        onClose={closeNotesModal}
        recordName={orderIdForNotes?.name}
        openTaskCount={orderIdForNotes?.open_task_count}
        key={orderIdForNotes?.id}
        orderId={orderIdForNotes?.id}
        isNoteAvailable={orderIdForNotes?.is_note_available}
        setOrderIdForNotes={setOrderIdForNotes}
        orderType={orderType}
        getConsignmentByRule={getConsignmentByRule}
        selectedCurrentView={selectedCurrentView}
      />
      <FilesDrawerShell
        isOpen={isOpenFiles && Boolean(orderIdForFiles)}
        onClose={closeFilesDrawer}
        orderId={orderIdForFiles?.id}
        orderName={orderIdForFiles?.name}
        recordDisplayName={orderIdForFiles?.recordDisplayName}
        orderType={orderType}
        getConsignmentByRule={getConsignmentByRule}
      />
      <BoardRelationshipGraphDrawer
        orderType={orderType}
        isOpen={Boolean(graphRecord)}
        onClose={closeGraphDrawer}
        record={graphRecord}
        onOpenFiles={openFilesDrawer}
        onOpenNotes={openNotesModal}
      />
    </>
  );
}
